import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../models/api.response.model';
import { Error } from '../models/error.model';
import { Message } from '../models/message.model';
import { Company } from '../models/company.model';
import { Invoice } from '../models/invoice.model';
import { Currency } from '../models/currency.model';
import { Address } from '../models/address.model';
import { CompanyInvoices } from '../models/company.invoices.model';
import { appToken } from '../scripts/app.token.script';

export class InvoiceDA {
  static apiResponse: ApiResponse

  public static async getInvoices(id_company: string, interval: string, start_date: string, end_date: string, contact_id: string, validity: string, status: string, expiration: string, payment: string, sort_field: string, sort_mode: string, group_by: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoicesByCompany: CompanyInvoices | null = null
      let invoiceGroupsByCompany: CompanyInvoices | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoices.list.data.json').then(response => { invoicesByCompany = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/sale.invoices.group.data.json').then(response => { invoiceGroupsByCompany = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoicesByCompany!.id_company === id_company || invoiceGroupsByCompany!.id_company === id_company) {
        if (group_by.length === 0) {
          invoicesByCompany!.filter.interval = interval
          invoicesByCompany!.filter.start_date = start_date
          invoicesByCompany!.filter.end_date = end_date
          invoicesByCompany!.filter.contact = contact_id
          invoicesByCompany!.filter.validity = validity
          invoicesByCompany!.filter.status = status
          invoicesByCompany!.filter.expiration = expiration
          invoicesByCompany!.filter.payment = payment
          invoicesByCompany!.filter.sort_field = sort_field
          invoicesByCompany!.filter.sort_mode = sort_mode
          invoicesByCompany!.filter.group_by = group_by
        } else {
          invoiceGroupsByCompany!.filter.interval = interval
          invoiceGroupsByCompany!.filter.start_date = start_date
          invoiceGroupsByCompany!.filter.end_date = end_date
          invoiceGroupsByCompany!.filter.contact = contact_id
          invoiceGroupsByCompany!.filter.validity = validity
          invoiceGroupsByCompany!.filter.status = status
          invoiceGroupsByCompany!.filter.expiration = expiration
          invoiceGroupsByCompany!.filter.payment = payment
          invoiceGroupsByCompany!.filter.sort_field = sort_field
          invoiceGroupsByCompany!.filter.sort_mode = sort_mode
          invoiceGroupsByCompany!.filter.group_by = group_by
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? invoicesByCompany : invoiceGroupsByCompany
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let startOfDay: Date = new Date(new Date().setHours(0, 0, 0, 0))
      let endOfDay: Date = new Date(new Date().setHours(23, 59, 59, 999))
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices?interval=${interval}&start_date=${start_date}&end_date=${end_date}&contact=${contact_id}&validity=${validity}&status=${status}&expiration=${expiration}&payment=${payment}&sort_field=${sort_field}&sort_mode=${sort_mode}&group_by=${group_by}&start_day=${startOfDay}&end_day=${endOfDay}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async getInvoice(id_company: string, id_invoice: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoice(id_company: string, customer_id: string, concerning: string, date: string, valid_until: string, currency: Currency, treasury_account_id: string, language: string, qr_type: string, requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_rate: string, rebate_type: string, rebate_value: string, rebate_quantity: string, unit: string }[] }[], signatures: string[], company_data: { uuid: string, name: string, logo: string, address: Address }, reference: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        company: company_data,
        customer: customer_id,
        valid_until: valid_until,
        currency: currency,
        treasury_account: treasury_account_id,
        requirements: requirements,
        created_date: date,
        language: language,
        qr_type: qr_type,
        reference: reference
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoiceReference(id_company: string, id_order: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.order.id === id_order) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_order}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoiceReminder(id_company: string, id_invoice: string, description: string, amount: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        invoice!.status = 'reminder'
        invoice!.reminders.push({
          id: `reminder${(Math.floor(Math.random() * 1000)).toString()}`,
          description: description,
          amount: (amount.length > 0) ? Number(amount) : 0,
          created_at: `${new Date(format((new Date()), 'yyyy-MM-dd HH:mm:ss zzzz'))}`,
          updated_at: `${new Date(format((new Date()), 'yyyy-MM-dd HH:mm:ss zzzz'))}`
        })

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}/reminders`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      let data: any = {
        description: description,
        amount: amount
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoice(id_company: string, id_invoice: string, concerning: string, date: string, valid_until: string, currency: Currency, treasury_account_id: string, language: string, qr_type: string, requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_rate: string, rebate_type: string, rebate_value: string, rebate_quantity: string, unit: string }[] }[], signatures: string[]): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        requirements: requirements,
        valid_until: valid_until,
        language: language,
        qr_type: qr_type,
        currency: currency,
        treasury_account: treasury_account_id,
        created_date: date
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoiceStatus(id_company: string, id_invoice: string, amount: number | null, company: {subject_tax: boolean, vat_debt_method: string, tax_method: string, tax_rate: string} | null, content_accounts: {id_content: string, id_account_credit: string, id_account_debit: string}[] | null): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        invoice!.validated = (invoice!.validated) ? false : true

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}/status`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      let data: any = {
        amount: amount,
        company: company,
        content_accounts: content_accounts
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoiceReminder(id_company: string, id_invoice: string, id_reminder: string, description: string, amount: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.reminders.some(item => item.id === id_reminder)) {
        invoice!.reminders = invoice!.reminders.map(item => {
          if (item.id === id_reminder) {
            item.description = description
            item.amount = Number(amount)
            item.updated_at =`${new Date(format((new Date()), 'yyyy-MM-dd HH:mm:ss zzzz'))}`
          }
          return item
        })

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}/reminders/${id_reminder}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      let data: any = {
        description: description,
        amount: amount
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteInvoiceReminder(id_company: string, id_invoice: string, id_reminder: string): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data.error }).catch(error => { alert(error) })

      if (invoice!.reminders.some(item => item.id === id_reminder)) {
        invoice!.reminders = invoice!.reminders.filter(item => item.id !== id_reminder)

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_BACKEND}/${id_company}/invoices/${id_invoice}/reminders/${id_reminder}`

      let config: any = {
        headers: { Authorization: appToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }
}
